import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from '../../utils/axios';
import { format, subMonths, subWeeks, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from 'recharts';

// Update color constants with new categories
const CATEGORY_COLORS = {
  'yoga ashtanga': '#4A601C',    // BlueViolet
  'yoga vinyasa': '#3CB371',     // MediumSeaGreen
  'yoga restauratif': '#FF69B4', // HotPink
  'yin yoga': '#032D23',         // DarkGreen
  'yoga des énergies': '#4A601C', // BlueViolet
  'shiatsu': '#890857',         // DarkRed
  'chi nei tsang': '#FFA500',   // Orange
  'soin': '#B22222',            // FireBrick
  'ateliers': '#800000',        // Maroon
  'pilates doux': '#6D2348',    // Indigo
  'power pilates': '#432C5F',   // Purple
  'autres': '#FFD700'           // Gold
};

// Helper function to get color for category
const getCategoryColor = (category) => {
  if (!category) return CATEGORY_COLORS.autres;
  const normalizedCategory = category.toLowerCase().trim();
  return CATEGORY_COLORS[normalizedCategory] || CATEGORY_COLORS.autres;
};

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && children}
  </div>
);

const INITIAL_STATE = {
  activities: {
    total: 0,
    byCategory: {},
    upcoming: 0,
    data: [],
    capacity: {
      totalCapacity: 0,
      usedCapacity: 0,
      unusedCapacity: 0,
      byCategory: {}
    },
    trends: []
  },
  users: {
    total: 0,
    active: 0,
    data: [],
    bookings: [],
    totalRemainingActivities: 0,
    averageUsage: 0
  }
};

const Statistics = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [timeRange, setTimeRange] = useState('weekly');
  const [stats, setStats] = useState(INITIAL_STATE);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/statistics');
      setStats({
        activities: {
          ...INITIAL_STATE.activities,
          ...response.data.activities
        },
        users: {
          ...INITIAL_STATE.users,
          ...response.data.users
        }
      });
      setError(null);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      setError(t('errorLoadingStats'));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleTimeRangeChange = (event, newRange) => {
    if (newRange !== null) {
      setTimeRange(newRange);
    }
  };

  const filterDataByTimeRange = (data, dateField) => {
    const now = new Date();
    let startDate;

    switch (timeRange) {
      case 'yearly':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      case 'monthly':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'weekly':
        startDate = startOfWeek(now, { weekStartsOn: 1 }); // Start from Monday
        break;
      default:
        startDate = startOfWeek(now, { weekStartsOn: 1 });
    }

    return data.filter(item => {
      const itemDate = new Date(item[dateField]);
      return itemDate >= startDate;
    });
  };

  const groupDataByDate = (data, dateField, valueFields) => {
    const groupedData = {};
    
    data.forEach(item => {
      const date = format(new Date(item[dateField]), 
        timeRange === 'yearly' ? 'yyyy' : 
        timeRange === 'monthly' ? 'MMM yyyy' : 
        'dd/MM/yyyy',
        { locale: fr }
      );

      // Initialize the date entry if it doesn't exist
      if (!groupedData[date]) {
        groupedData[date] = { date };
        
        // For activities, initialize category counts
        if (item.category) {
          Object.keys(CATEGORY_COLORS).forEach(cat => {
            groupedData[date][cat] = 0;
          });
        }
        // For users, initialize user counts
        else {
          groupedData[date].total = 0;
          groupedData[date].active = 0;
        }
      }

      // Handle activity data
      if (item.category) {
        const category = item.category.toLowerCase();
        if (groupedData[date][category] !== undefined) {
          groupedData[date][category] += 1;
        }
      }
      // Handle user data
      else {
        valueFields.forEach(field => {
          if (item[field] !== undefined) {
            groupedData[date][field] += item[field];
          }
        });
      }
    });

    return Object.values(groupedData);
  };

  const getActivityData = () => {
    if (!stats.activities.data) return [];
    
    const filteredData = filterDataByTimeRange(stats.activities.data, 'date');
    return filteredData.map(item => {
      const result = { date: item.date };
      Object.keys(CATEGORY_COLORS).forEach(category => {
        result[category] = item[category] || 0;
      });
      return result;
    });
  };

  const getUserData = () => {
    const filteredData = filterDataByTimeRange(stats.users.data || [], 'date');
    return groupDataByDate(filteredData, 'date', ['total', 'active']);
  };

  const getCategoryStats = () => {
    if (!stats.activities.capacity?.byCategory) return {};
    
    const categoryStats = {};
    Object.entries(stats.activities.capacity.byCategory).forEach(([category, data]) => {
      categoryStats[category] = data.totalCapacity || 0;
    });
    
    return categoryStats;
  };

  const getUserBookingsData = () => {
    if (!stats.users.bookings || !Array.isArray(stats.users.bookings)) return [];
    
    return stats.users.bookings
      .map(user => {
        // Filter bookings based on time range
        const filteredBookings = (user.bookings || []).filter(booking => {
          const bookingDate = new Date(booking.date);
          const now = new Date();
          
          switch (timeRange) {
            case 'yearly':
              return bookingDate.getFullYear() === now.getFullYear();
            case 'monthly':
              return bookingDate.getMonth() === now.getMonth() && 
                     bookingDate.getFullYear() === now.getFullYear();
            case 'weekly':
            default:
              const weekStart = startOfWeek(now, { weekStartsOn: 1 });
              return bookingDate >= weekStart;
          }
        });

        // Calculate booking status counts
        const status = filteredBookings.reduce((acc, booking) => {
          const status = booking.status || 'registered';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {
          registered: 0,
          attended: 0,
          cancelled: 0
        });

        // Calculate category counts
        const categories = filteredBookings.reduce((acc, booking) => {
          const category = booking.category?.toLowerCase() || 'autres';
          acc[category] = (acc[category] || 0) + 1;
          return acc;
        }, {});

        return {
          id: user.id,
          name: user.name || t('anonymous'),
          email: user.email || t('noEmail'),
          bookingsCount: filteredBookings.length,
          status,
          categories
        };
      })
      .filter(user => user.bookingsCount > 0)
      .sort((a, b) => b.bookingsCount - a.bookingsCount);
  };

  const getUserTrendsData = () => {
    const filteredData = filterDataByTimeRange(stats.users.data || [], 'date');
    const bookingsData = stats.users.bookings || [];
    
    const groupedData = groupDataByDate(filteredData, 'date', ['total', 'active']);
    
    // Add bookings count to each date
    groupedData.forEach(dateData => {
      const date = new Date(dateData.date);
      const bookingsCount = bookingsData.reduce((total, user) => {
        const bookingsInDate = (user.bookings || []).filter(booking => {
          const bookingDate = new Date(booking.date);
          return bookingDate.toDateString() === date.toDateString();
        }).length;
        return total + bookingsInDate;
      }, 0);
      dateData.bookings = bookingsCount;
    });
    
    return groupedData;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('statistics')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab label={t('overview')} />
          <Tab label={t('activities')} />
          <Tab label={t('users')} />
        </Tabs>
      </Box>

      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={handleTimeRangeChange}
          size="small"
        >
          <ToggleButton value="weekly">{t('weekly')}</ToggleButton>
          <ToggleButton value="monthly">{t('monthly')}</ToggleButton>
          <ToggleButton value="yearly">{t('yearly')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <TabPanel value={selectedTab} index={0}>
        <Grid container spacing={3}>
          {/* Capacity Overview Cards */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('totalCapacity')}
                </Typography>
                <Typography variant="h3" color="primary">
                  {stats.activities.capacity?.totalCapacity || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('totalAvailableSpots')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('usedCapacity')}
                </Typography>
                <Typography variant="h3" color="success.main">
                  {stats.activities.capacity?.usedCapacity || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('totalBookedSpots')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('unusedCapacity')}
                </Typography>
                <Typography variant="h3" color="error.main">
                  {stats.activities.capacity?.unusedCapacity || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('totalUnusedSpots')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Activity Distribution Chart */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('activityDistribution')}
                </Typography>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={getActivityData()}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {Object.keys(CATEGORY_COLORS).map(category => (
                        <Bar
                          key={category}
                          dataKey={category}
                          name={t(category)}
                          stackId="a"
                          fill={CATEGORY_COLORS[category]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Capacity by Category Table */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('capacityByCategory')}
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('category')}</TableCell>
                        <TableCell align="right">{t('totalCapacity')}</TableCell>
                        <TableCell align="right">{t('usedCapacity')}</TableCell>
                        <TableCell align="right">{t('unusedCapacity')}</TableCell>
                        <TableCell align="right">{t('usagePercentage')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(stats.activities.capacity?.byCategory || {}).map(([category, data]) => {
                        const usagePercentage = data.totalCapacity > 0
                          ? ((data.usedCapacity / data.totalCapacity) * 100).toFixed(1)
                          : '0.0';
                        return (
                          <TableRow key={category}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: getCategoryColor(category)
                                  }}
                                />
                                {t(category)}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{data.totalCapacity}</TableCell>
                            <TableCell align="right">{data.usedCapacity}</TableCell>
                            <TableCell align="right">{data.unusedCapacity}</TableCell>
                            <TableCell align="right">{usagePercentage}%</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={selectedTab} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('activityTrends')}
                </Typography>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={stats.activities.trends || []}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="availableSpots"
                        name={t('availableSpots')}
                        stroke="#2196F3"
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="participants"
                        name={t('participants')}
                        stroke="#4CAF50"
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="unusedSpots"
                        name={t('unusedSpots')}
                        stroke="#F44336"
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={selectedTab} index={2}>
        <Grid container spacing={3}>
          {/* User Statistics Cards */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('totalUsers')}
                </Typography>
                <Typography variant="h3" color="primary">
                  {stats.users.total || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('activeUsers')}
                </Typography>
                <Typography variant="h3" color="success.main">
                  {stats.users.active || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('remainingActivities')}
                </Typography>
                <Typography variant="h3" color="info.main">
                  {stats.users.totalRemainingActivities || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('averageUsage')}
                </Typography>
                <Typography variant="h3" color="warning.main">
                  {(stats.users.averageUsage || 0).toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* User Trends Chart */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('userTrends')}
                </Typography>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={stats.users.data || []}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="total"
                        name={t('totalUsers')}
                        stroke="#2196F3"
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="active"
                        name={t('activeUsers')}
                        stroke="#4CAF50"
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="remainingActivities"
                        name={t('remainingActivities')}
                        stroke="#FFA726"
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* User Bookings Table */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('userBookings')}
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('name')}</TableCell>
                        <TableCell>{t('email')}</TableCell>
                        <TableCell align="right">{t('registered')}</TableCell>
                        <TableCell align="right">{t('attended')}</TableCell>
                        <TableCell align="right">{t('cancelled')}</TableCell>
                        {Object.keys(CATEGORY_COLORS).map(category => (
                          <TableCell key={category} align="right">
                            {t(category)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getUserBookingsData().map((user) => (
                        <TableRow key={user.id || `${user.email}-${user.name}`}>
                          <TableCell>{user.name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell align="right">{user.status.registered}</TableCell>
                          <TableCell align="right">{user.status.attended}</TableCell>
                          <TableCell align="right">{user.status.cancelled}</TableCell>
                          {Object.keys(CATEGORY_COLORS).map(category => (
                            <TableCell key={category} align="right">
                              {user.categories[category] || 0}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                      {getUserBookingsData().length === 0 && (
                        <TableRow>
                          <TableCell colSpan={Object.keys(CATEGORY_COLORS).length + 5} align="center">
                            {t('noActivitiesInRange')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Container>
  );
};

export default Statistics; 